import { useState, useCallback } from 'react';
import { ShoppingCart } from 'lucide-react';
import { toast } from '@/store/toastStore';
import {
  type ApptProductType,
  type ApptProductSlot,
  type AppointmentProductsResponse,
  consumeAppointmentProduct,
  returnAppointmentProduct,
  compAppointmentProduct,
} from '../../services/patientProductTypes.service';
import { getErrorMessage } from '../../services/apiClient';
import { parseCurrency, formatCurrency } from '@/lib/utils';

interface Props {
  patientId: string;
  apptID: string;
  data: AppointmentProductsResponse | null;
  loading: boolean;
  error?: string | null;
  onRefresh: () => void;
  showProductButtons: boolean;
  onAddProducts?: () => void;
  onPurchase?: () => void;
  compOverride?: boolean;
  onAutoSaveStart?: () => void;
  onAutoSaveEnd?: () => void;
  isCheckedOut?: boolean;
}

function classifyAvailable(products: ApptProductSlot[]) {
  const available: ApptProductSlot[] = [];
  const addons: ApptProductSlot[] = [];
  const upcoming: ApptProductSlot[] = [];
  const upcomingaddons: ApptProductSlot[] = [];

  for (const p of products) {
    const isAddon =
      !!p.appointmentProductAddonsID &&
      p.appointmentProductAddonsID !== '' &&
      p.appointmentProductAddonsID !== '0';
    const isUpcoming =
      (!p.paymentMaterialID || p.paymentMaterialID === '0') &&
      (!p.membershipProductID || p.membershipProductID === '0') &&
      (!p.packageProductID || p.packageProductID === '0');

    if (isAddon) {
      (isUpcoming ? upcomingaddons : addons).push(p);
    } else if (isUpcoming) {
      upcoming.push(p);
    } else {
      available.push(p);
    }
  }
  return { available, addons, upcoming, upcomingaddons };
}

function classifyPending(products: ApptProductSlot[]) {
  const purchase: ApptProductSlot[] = [];
  const addons: ApptProductSlot[] = [];
  for (const p of products) {
    const isAddon =
      !!p.appointmentProductAddonsID &&
      p.appointmentProductAddonsID !== '' &&
      p.appointmentProductAddonsID !== '0';
    (isAddon ? addons : purchase).push(p);
  }
  return { purchase, addons };
}

function classifyDisbursed(products: ApptProductSlot[]) {
  const disbursed: ApptProductSlot[] = [];
  const comp: ApptProductSlot[] = [];
  const compaddons: ApptProductSlot[] = [];
  const addons: ApptProductSlot[] = [];

  for (const p of products) {
    const isAddon =
      !!p.appointmentProductAddonsID &&
      p.appointmentProductAddonsID !== '' &&
      p.appointmentProductAddonsID !== '0';
    if (p.type === 'comp') {
      (isAddon ? compaddons : comp).push(p);
    } else if (isAddon) {
      addons.push(p);
    } else {
      disbursed.push(p);
    }
  }
  return { disbursed, comp, compaddons, addons };
}

function QuantitySelect({
  max,
  value,
  onChange,
}: {
  max: number;
  value: number;
  onChange: (v: number) => void;
}) {
  if (max <= 1) return null;
  return (
    <select
      className="rounded border border-gray-300 px-1 py-0.5 text-xs"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  );
}

function AvailableRow({
  label,
  subLabel,
  products,
  isUpcoming,
  isAddon,
  showButtons,
  onConsume,
  onComp,
  onRemoveAddon,
}: {
  label: string;
  subLabel: string;
  products: ApptProductSlot[];
  isUpcoming: boolean;
  isAddon: boolean;
  showButtons: boolean;
  onConsume: (products: ApptProductSlot[], qty: number) => void;
  onComp: (products: ApptProductSlot[], qty: number) => void;
  onRemoveAddon: (products: ApptProductSlot[], qty: number) => void;
}) {
  const [qty, setQty] = useState(products.length);
  if (products.length === 0) return null;

  return (
    <div
      className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm ${isUpcoming ? 'bg-orange-100' : 'bg-green-50'}`}
    >
      <span className="flex-1 font-medium">
        {label} (x{products.length}) {subLabel}
      </span>
      {isUpcoming && products[0]?.addedDate && (
        <span className="text-xs text-gray-500">
          Available: {products[0].addedDate}
        </span>
      )}
      {showButtons && (
        <>
          <button
            type="button"
            className="rounded bg-gray-200 px-2 py-0.5 text-xs hover:bg-gray-300"
            onClick={() => onComp(products, qty)}
          >
            Comp
          </button>
          <QuantitySelect
            max={products.length}
            value={qty}
            onChange={setQty}
          />
          {!isUpcoming && (
            <button
              type="button"
              className="rounded bg-blue-500 px-2 py-0.5 text-xs text-white hover:bg-blue-600"
              onClick={() => onConsume(products, qty)}
              title="Use"
            >
              Use
            </button>
          )}
          {isAddon && (
            <button
              type="button"
              className="text-red-400 hover:text-red-600"
              onClick={() => onRemoveAddon(products, qty)}
              title="Remove"
            >
              &times;
            </button>
          )}
        </>
      )}
    </div>
  );
}

function PurchaseRow({
  label,
  subLabel,
  products,
  curRate,
  isAddon,
  showButtons,
  onComp,
  onRemoveAddon,
}: {
  label: string;
  subLabel: string;
  products: ApptProductSlot[];
  curRate: number;
  isAddon: boolean;
  showButtons: boolean;
  onComp: (products: ApptProductSlot[], qty: number) => void;
  onRemoveAddon: (products: ApptProductSlot[], qty: number) => void;
}) {
  const [qty, setQty] = useState(products.length);
  if (products.length === 0) return null;

  return (
    <div className="flex items-center gap-2 rounded bg-gray-200 px-2 py-1.5 text-sm">
      <span className="flex-1 font-medium">
        {label} (x{products.length}) {subLabel}
      </span>
      <span className="text-xs text-gray-600">
        {formatCurrency(parseCurrency(curRate))} x {products.length}
      </span>
      {showButtons && (
        <>
          <button
            type="button"
            className="rounded bg-gray-300 px-2 py-0.5 text-xs hover:bg-gray-400"
            onClick={() => onComp(products, qty)}
          >
            Comp
          </button>
          <QuantitySelect
            max={products.length}
            value={qty}
            onChange={setQty}
          />
          {isAddon && (
            <button
              type="button"
              className="text-red-400 hover:text-red-600"
              onClick={() => onRemoveAddon(products, qty)}
              title="Remove"
            >
              &times;
            </button>
          )}
        </>
      )}
    </div>
  );
}

function DisbursedRow({
  label,
  subLabel,
  products,
  disbursedType,
  showButtons,
  onReturn,
  onComp,
  onRemoveAddon,
}: {
  label: string;
  subLabel: string;
  products: ApptProductSlot[];
  disbursedType: string;
  showButtons: boolean;
  onReturn: (products: ApptProductSlot[], qty: number) => void;
  onComp: (products: ApptProductSlot[], qty: number) => void;
  onRemoveAddon: (products: ApptProductSlot[], qty: number) => void;
}) {
  const [qty, setQty] = useState(products.length);
  if (products.length === 0) return null;

  const isComp = disbursedType === 'comp' || disbursedType === 'compaddons';
  const isAddon = disbursedType === 'addons' || disbursedType === 'compaddons';

  return (
    <div className="flex items-center gap-2 rounded bg-yellow-50 px-2 py-1.5 text-sm">
      <span className="flex-1 font-medium">
        {label} (x{products.length}) {subLabel}
      </span>
      {showButtons && (
        <>
          {!isComp ? (
            <button
              type="button"
              className="rounded bg-gray-200 px-2 py-0.5 text-xs hover:bg-gray-300"
              onClick={() => onComp(products, qty)}
            >
              Comp
            </button>
          ) : (
            <span className="px-2 py-0.5 text-xs text-gray-400">Comp</span>
          )}
          <QuantitySelect
            max={products.length}
            value={qty}
            onChange={setQty}
          />
          <button
            type="button"
            className="rounded bg-orange-500 px-2 py-0.5 text-xs text-white hover:bg-orange-600"
            onClick={() => onReturn(products, qty)}
            title="Return"
          >
            Return
          </button>
          {isAddon && (
            <button
              type="button"
              className="text-red-400 hover:text-red-600"
              onClick={() => onRemoveAddon(products, qty)}
              title="Remove"
            >
              &times;
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ProductTypeSection({
  // ptId is passed by the caller (and used there as the React key) but
  // never read in here, so it is left off the destructure and kept on the
  // props type.
  ptData,
  patientId,
  apptID,
  showButtons,
  onRefresh,
  parentDisabled,
  onAutoSaveStart,
  onAutoSaveEnd,
}: {
  ptId: string;
  ptData: ApptProductType;
  patientId: string;
  apptID: string;
  showButtons: boolean;
  onRefresh: () => void;
  parentDisabled?: boolean;
  onAutoSaveStart?: () => void;
  onAutoSaveEnd?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const avail = classifyAvailable(ptData.pendingAvailable);
  const purch = classifyPending(ptData.pending);
  const disb = classifyDisbursed(ptData.disbursed);

  const noPending =
    ptData.pending.length === 0 && ptData.pendingAvailable.length === 0;
  const satisfiedCount =
    ptData.pendingAvailable.length + ptData.disbursed.length;

  const wrap = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      onAutoSaveStart?.();
      try {
        await fn();
        onRefresh();
      } catch (err: unknown) {
        const msg = getErrorMessage(err, 'Product action failed');
        console.error('[AppointmentProducts] action failed', msg, err);
        toast.error(msg);
      } finally {
        setBusy(false);
        onAutoSaveEnd?.();
      }
    },
    [onRefresh, onAutoSaveStart, onAutoSaveEnd],
  );

  const handleConsume = (products: ApptProductSlot[], qty: number) =>
    wrap(() =>
      consumeAppointmentProduct(patientId, {
        apptID,
        productJson: products.slice(0, qty),
        quantity: qty,
      }),
    );

  const handleComp = (products: ApptProductSlot[], qty: number) =>
    wrap(() =>
      compAppointmentProduct(patientId, {
        apptID,
        productJson: products.slice(0, qty),
        quantity: qty,
      }),
    );

  const handleCompDisbursed = (products: ApptProductSlot[], qty: number) =>
    wrap(() =>
      returnAppointmentProduct(patientId, {
        apptID,
        productJson: products.slice(0, qty),
        quantity: qty,
        type: 'comp',
      }),
    );

  const handleReturn = (products: ApptProductSlot[], qty: number) =>
    wrap(() =>
      returnAppointmentProduct(patientId, {
        apptID,
        productJson: products.slice(0, qty),
        quantity: qty,
        type: 'pending',
      }),
    );

  const handleRemoveAddon = (products: ApptProductSlot[], qty: number) =>
    wrap(() =>
      returnAppointmentProduct(patientId, {
        apptID,
        productJson: products.slice(0, qty),
        quantity: qty,
        type: 'remove-addons',
      }),
    );

  const addonsTitle =
    ptData.addonsQuantity >= 1
      ? ` (${ptData.addonsQuantity} Add-on${ptData.addonsQuantity > 1 ? 's' : ''})`
      : '';

  return (
    <div className="mb-1 rounded border border-gray-200">
      <div
        className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50"
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={`text-xs transition-transform ${open ? 'rotate-90' : ''}`}
        >
          &#9654;
        </span>
        <span className="flex-1 font-semibold text-sm">
          {ptData.productTypeName}
          {addonsTitle && (
            <span className="ml-1 font-normal text-green-600">
              {addonsTitle}
            </span>
          )}
        </span>
        {noPending ? (
          <span className="text-green-600">&#10003;</span>
        ) : (
          <span
            className={
              ptData.pending.length === 0 ? 'text-green-600' : 'text-red-500'
            }
          >
            {satisfiedCount} / {ptData.quantity}
          </span>
        )}
      </div>

      {open && (
        <div
          className={`space-y-1 px-3 pb-2 ${busy || parentDisabled ? 'pointer-events-none opacity-50' : ''}`}
        >
          {ptData.pendingAvailable.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold text-gray-500">
                Available Products
              </div>
              {(
                Object.entries(avail) as [string, ApptProductSlot[]][]
              ).map(([key, arr]) => {
                const subLabel =
                  key === 'upcoming'
                    ? '(Upcoming)'
                    : key === 'addons'
                      ? '(Add-on)'
                      : key === 'upcomingaddons'
                        ? '(Upcoming Add-on)'
                        : '';
                return (
                  <AvailableRow
                    key={key}
                    label={ptData.productTypeName}
                    subLabel={subLabel}
                    products={arr}
                    isUpcoming={key === 'upcoming' || key === 'upcomingaddons'}
                    isAddon={key === 'addons' || key === 'upcomingaddons'}
                    showButtons={showButtons && apptID !== ''}
                    onConsume={handleConsume}
                    onComp={handleComp}
                    onRemoveAddon={handleRemoveAddon}
                  />
                );
              })}
            </div>
          )}

          {ptData.pending.length > 0 && ptData.hasCostItem === false && (
            <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-sm text-amber-800">
              {ptData.productTypeName} (x{ptData.pending.length}) has no active
              price and can&apos;t be purchased. Switch the required set above.
            </div>
          )}

          {ptData.pending.length > 0 && ptData.hasCostItem !== false && (
            <div>
              <div className="mb-1 text-xs font-semibold text-gray-500">
                Additional {ptData.productTypeName} required:{' '}
                {ptData.pending.length}
              </div>
              {(
                Object.entries(purch) as [string, ApptProductSlot[]][]
              ).map(([key, arr]) => (
                <PurchaseRow
                  key={key}
                  label={ptData.productTypeName}
                  subLabel={key === 'addons' ? '(Add-on)' : ''}
                  products={arr}
                  curRate={ptData.curRate}
                  isAddon={key === 'addons'}
                  showButtons={showButtons && apptID !== ''}
                  onComp={handleComp}
                  onRemoveAddon={handleRemoveAddon}
                />
              ))}
            </div>
          )}

          {ptData.disbursed.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold text-gray-500">
                Disbursed {ptData.productTypeName}: {ptData.disbursed.length}
              </div>
              {(
                Object.entries(disb) as [string, ApptProductSlot[]][]
              ).map(([key, arr]) => {
                const subLabel =
                  key === 'comp'
                    ? '(Comp)'
                    : key === 'addons'
                      ? '(Add-on)'
                      : key === 'compaddons'
                        ? '(Comp Add-on)'
                        : '';
                return (
                  <DisbursedRow
                    key={key}
                    label={ptData.productTypeName}
                    subLabel={subLabel}
                    products={arr}
                    disbursedType={key}
                    showButtons={showButtons && apptID !== ''}
                    onReturn={handleReturn}
                    onComp={handleCompDisbursed}
                    onRemoveAddon={handleRemoveAddon}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AppointmentProductsPanel({
  patientId,
  apptID,
  data,
  loading,
  error,
  onRefresh,
  showProductButtons,
  onAddProducts,
  onPurchase,
  compOverride,
  onAutoSaveStart,
  onAutoSaveEnd,
  isCheckedOut = false,
}: Props) {
  const [busy, setBusy] = useState(false);
  const refreshing = loading && !!data;
  const isCompedRaw = compOverride || (data?.compAppointment ?? false);

  if (loading && !data) {
    return (
      <div className="py-4 text-center text-sm text-gray-400">
        Loading products...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        Failed to load products: {error}
        <button
          type="button"
          className="ml-2 text-xs underline hover:text-red-900"
          onClick={onRefresh}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    if (apptID === '' && onAddProducts) {
      return (
        <div className="text-xs text-gray-500 italic">
          Save the appointment to add products.
        </div>
      );
    }
    return null;
  }

  const products = data.products;
  const hasProducts = Object.keys(products).length > 0;
  const hasAnySlots = Object.values(products).some(
    (pt) => pt.pending.length > 0 || pt.pendingAvailable.length > 0 || pt.disbursed.length > 0 || pt.purchaseOthers.length > 0,
  );
  const isComped = hasAnySlots && isCompedRaw;
  const allPendingAvailable = Object.values(products).flatMap(
    (pt) => pt.pendingAvailable,
  );

  const needsPurchase = Object.values(products).some(
    (pt) =>
      pt.hasCostItem !== false &&
      (pt.pending.length > 0 || pt.purchaseOthers.length > 0),
  );
  // Required product types that are attached but have no active cost item —
  // they can't be purchased; the user must switch the required set.
  const hasUnpurchasable = Object.values(products).some(
    (pt) => pt.hasCostItem === false && pt.pending.length > 0,
  );
  const estimatedAmount = Object.values(products).reduce(
    (sum, pt) => sum + parseCurrency(pt.curRate) * pt.pending.length,
    0,
  );
  const additionalAmount = Object.values(products).reduce(
    (sum, pt) => sum + parseCurrency(pt.curRate) * pt.purchaseOthers.length,
    0,
  );
  const totalEstimated = estimatedAmount + additionalAmount;

  const purchaseOtherNames = Object.values(products)
    .filter((pt) => pt.purchaseOthers.length > 0)
    .map((pt) => `${pt.productTypeName} (x${pt.purchaseOthers.length})`);

  const handleDisburseAll = async () => {
    if (allPendingAvailable.length === 0) return;
    setBusy(true);
    onAutoSaveStart?.();
    try {
      await consumeAppointmentProduct(patientId, {
        apptID,
        productJson: allPendingAvailable,
        quantity: allPendingAvailable.length,
      });
      onRefresh();
    } catch (err) {
      console.error('[AppointmentProducts] disburse all failed', err);
      toast.error(getErrorMessage(err, 'Disburse products failed'));
    } finally {
      setBusy(false);
      onAutoSaveEnd?.();
    }
  };

  const handleCompAll = async () => {
    setBusy(true);
    onAutoSaveStart?.();
    try {
      await compAppointmentProduct(patientId, { apptID, type: 'all' });
      onRefresh();
    } catch (err) {
      console.error('[AppointmentProducts] comp all failed', err);
      toast.error(getErrorMessage(err, 'Comp products failed'));
    } finally {
      setBusy(false);
      onAutoSaveEnd?.();
    }
  };

  const handleUncompAll = async () => {
    setBusy(true);
    onAutoSaveStart?.();
    try {
      await compAppointmentProduct(patientId, { apptID, type: 'uncomp' });
      onRefresh();
    } catch (err) {
      console.error('[AppointmentProducts] uncomp failed', err);
      toast.error(getErrorMessage(err, 'Uncomp products failed'));
    } finally {
      setBusy(false);
      onAutoSaveEnd?.();
    }
  };

  const disabled = busy || refreshing;

  return (
    <div className="space-y-2">
      {hasProducts && showProductButtons && apptID !== '' && (
        <div className={`flex items-center justify-between ${disabled ? 'pointer-events-none opacity-60' : ''}`}>
          <div className="flex items-center gap-2">
            {isCheckedOut && allPendingAvailable.length > 0 && (
              <button
                type="button"
                className="rounded bg-blue-500 px-3 py-1 text-xs text-white hover:bg-blue-600"
                onClick={handleDisburseAll}
              >
                Disburse Products
              </button>
            )}
            {refreshing && (
              <div className="flex items-center gap-1.5">
                <svg className="h-3.5 w-3.5 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-xs text-blue-600">Updating...</span>
              </div>
            )}
          </div>
          {!isComped && (
            <button
              type="button"
              className="rounded bg-gray-200 px-3 py-1 text-xs hover:bg-gray-300"
              onClick={handleCompAll}
            >
              Comp Products
            </button>
          )}
        </div>
      )}

      {hasProducts && !isCheckedOut && allPendingAvailable.length > 0 && (
        <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
          Available products are reserved for this appointment and will be automatically disbursed upon checkout.
        </div>
      )}

      {hasProducts && isCheckedOut && allPendingAvailable.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This appointment has undisbursed products. They will remain reserved and are not available for use elsewhere, but they do require manual disbursement.
        </div>
      )}

      {hasProducts && (
        <div className="hidden" data-total-quantity={data.totalQuantity} data-total-disbursed={data.totalDisbursed} />
      )}

      {hasProducts && Object.entries(products).map(([ptId, ptData]) => (
        <ProductTypeSection
          key={ptId}
          ptId={ptId}
          ptData={ptData}
          patientId={patientId}
          apptID={apptID}
          showButtons={showProductButtons}
          onRefresh={onRefresh}
          parentDisabled={disabled}
          onAutoSaveStart={onAutoSaveStart}
          onAutoSaveEnd={onAutoSaveEnd}
        />
      ))}

      {hasUnpurchasable && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          Some required products for this appointment no longer have an active
          price and can&apos;t be purchased. Use the switch above to select the
          available required products.
        </div>
      )}

      <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
        {hasProducts && (isComped ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-amber-800">
                All products for this appointment are comped.
              </div>
              {showProductButtons && apptID !== '' && (
                <button
                  type="button"
                  className="rounded bg-amber-500 px-3 py-1 text-xs font-medium text-white hover:bg-amber-600"
                  onClick={handleUncompAll}
                >
                  Uncomp
                </button>
              )}
            </div>
          </div>
        ) : needsPurchase ? (
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="text-sm font-semibold text-gray-800">
                  Estimated Amount: {formatCurrency(totalEstimated)}
                  {data.isMember && (
                    <span className="ml-2 inline-block rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                      Member
                    </span>
                  )}
                </div>
                {purchaseOtherNames.length > 0 && (
                  <div className="text-xs text-gray-600">
                    <div>This patient has earlier appointments that require similar products</div>
                    <div className="font-semibold text-green-700">{purchaseOtherNames.join(', ')}</div>
                    <div>You must purchase all products.</div>
                  </div>
                )}
              </div>
              {showProductButtons && apptID !== '' && onPurchase && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded bg-blue-500 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-600"
                  onClick={onPurchase}
                >
                  <ShoppingCart className="h-3.5 w-3.5" />
                  Purchase
                </button>
              )}
            </div>
          </div>
        ) : hasUnpurchasable ? null : hasAnySlots ? (
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-green-800">
                This patient has enough products for this appointment.
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2.5">
            <div className="text-sm font-medium text-gray-600">
              No products required for this appointment.
            </div>
          </div>
        ))}

        {onAddProducts && (
          showProductButtons && apptID !== '' ? (
            <div className="pt-1 flex justify-end">
              <button
                type="button"
                className="rounded bg-green-500 px-3 py-1.5 text-xs text-white hover:bg-green-600"
                onClick={onAddProducts}
              >
                + Add Products
              </button>
            </div>
          ) : (
            <div className="pt-1 text-xs text-gray-500 italic">
              {apptID === '' ? 'Save the appointment to add products.' : 'Save changes to add products.'}
            </div>
          )
        )}
      </div>
    </div>
  );
}
